import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Sparkles, Server, Database } from 'lucide-react';
import { fadeInUp, staggerContainer } from '../lib/animations';

const philosophyItems = [
  {
    id: 1,
    icon: Layers,
    tool: 'Next.js & React',
    reason: 'Modern component architecture with server-side rendering for optimal performance and SEO. The ecosystem and community support make rapid development possible without sacrificing code quality.',
  },
  {
    id: 2,
    icon: Sparkles,
    tool: 'Framer Motion',
    reason: 'Declarative animations that feel natural and purposeful. Motion enhances user experience without compromising performance, creating interfaces that users enjoy interacting with.',
  },
  {
    id: 3,
    icon: Server,
    tool: 'Laravel',
    reason: 'Elegant syntax and robust architecture for building scalable APIs. Laravel\'s conventions and tooling accelerate backend development while maintaining clean, maintainable code.',
  },
  {
    id: 4,
    icon: Database,
    tool: 'PostgreSQL & MySQL',
    reason: 'Reliable, battle-tested relational databases for data integrity and complex queries. The right tool for applications where structure and relationships matter.',
  },
];

const ToolsPhilosophy = () => {
  return (
    <section id="philosophy" className="relative py-32 bg-[#0a0a0a] overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl sm:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
              Tools & Stack Philosophy
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Choosing the right tools isn't just about trends—it's about building
            maintainable, performant applications that solve real problems
          </p>
        </motion.div>

        {/* Philosophy Grid */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {philosophyItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.id}
                variants={fadeInUp}
                className="group relative"
              >
                <motion.div
                  whileHover={{ y: -4 }}
                  className="h-full p-8 bg-gradient-to-br from-white/5 to-white/[0.02] rounded-2xl border border-white/10 hover:border-cyan-500/50 transition-all duration-500"
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-cyan-500/0 group-hover:from-cyan-500/5 group-hover:to-blue-500/5 rounded-2xl transition-all duration-500 pointer-events-none" />
                  
                  <div className="relative">
                    {/* Icon & Tool Name */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-6 h-6 text-cyan-400" />
                      </div>
                      <h3 className="text-2xl font-bold text-white group-hover:text-cyan-400 transition-colors duration-300">
                        {item.tool}
                      </h3>
                    </div>

                    {/* Philosophy/Reason */}
                    <p className="text-gray-400 leading-relaxed">
                      {item.reason}
                    </p>
                  </div>

                  {/* Corner accent line */}
                  <div className="absolute bottom-0 right-0 w-32 h-1 bg-gradient-to-l from-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="text-lg text-gray-500 italic max-w-2xl mx-auto">
            "The best stack is the one that lets you ship quality products consistently—
            these tools have proven themselves in production."
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ToolsPhilosophy;
